import { ReactNode, isValidElement } from "react";
import { Box, Typography } from "@mui/material";
import { dash } from "../governanceStatus";
import { FAINT, TEXT } from "./ui";

export interface Field {
  label: string;
  /** Strings, numbers and empty values go through `dash()`; nodes render as is. */
  value: ReactNode;
  /** Span both columns (long text). */
  full?: boolean;
}

const renderValue = (value: ReactNode): ReactNode => {
  if (isValidElement(value)) return value;
  if (value === null || value === undefined || typeof value === "string" || typeof value === "number") {
    return dash(value as string | number | null | undefined);
  }
  return value;
};

/**
 * Read-only label / value pairs: one column on phones, two from `sm` (or
 * always one with `columns={1}`). Values keep their line breaks.
 */
const FieldGrid = ({ fields, columns = 2 }: { fields: Field[]; columns?: 1 | 2 }) => (
  <Box
    component="dl"
    sx={{
      display: "grid",
      gridTemplateColumns: columns === 2 ? { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" } : "minmax(0, 1fr)",
      columnGap: 3,
      rowGap: 1.5,
      m: 0,
    }}
  >
    {fields.map((f, i) => (
      <Box key={`${i}-${f.label}`} sx={{ minWidth: 0, gridColumn: f.full ? "1 / -1" : undefined }}>
        <Typography component="dt" variant="caption" sx={{ display: "block", color: FAINT, fontWeight: 600 }}>
          {f.label}
        </Typography>
        <Typography
          component="dd"
          variant="body2"
          sx={{ m: 0, color: TEXT, whiteSpace: "pre-line", overflowWrap: "anywhere" }}
        >
          {renderValue(f.value)}
        </Typography>
      </Box>
    ))}
  </Box>
);

export default FieldGrid;
